import { DataTexture, NearestFilter, RGBAFormat, UnsignedByteType } from "three";
import type { EdgeSegment } from "./dxf-loader";

/** Largest texture dimension (texels). Balances sharpness vs memory (≤ 2048²·4 = 16MB). */
const MAX_TEX_DIM = 2048;
/** dist∈[0,maxDist] → A∈[0,254]; A=255 = "no edge nearby". */
export const DXF_TEX_MAX_DIST = 10;
/** maxSegLen for the segment tree (only affects pruning, not the result). */
const MAX_SEG_LEN = 25;

export interface DxfTexture {
  texture: DataTexture;
  minX: number; minZ: number;
  spanX: number; spanZ: number;
  width: number; height: number;
}

/**
 * WORKER source (self-contained JS, no imports): builds a segment KD-tree from
 * the DXF edges then fills one row BAND [j0,j1) of the texture. Run several
 * workers in parallel so all heavy work stays OFF the main thread (no jank).
 */
const WORKER_SRC = `
function tessellate(ex, ez, color, maxLen) {
  const segs = [];
  const n = color.length;
  for (let e = 0; e < n; e++) {
    const x1 = ex[e*2], z1 = ez[e*2], x2 = ex[e*2+1], z2 = ez[e*2+1];
    const dx = x2 - x1, dz = z2 - z1, len = Math.hypot(dx, dz);
    const k = Math.max(1, Math.ceil(len / maxLen));
    let px = x1, pz = z1;
    for (let i = 1; i <= k; i++) {
      const t = i / k, nx = x1 + dx*t, nz = z1 + dz*t;
      segs.push({ x1: px, z1: pz, x2: nx, z2: nz, color: color[e] });
      px = nx; pz = nz;
    }
  }
  return segs;
}
function pointSegDist2(px, pz, s) {
  const dx = s.x2-s.x1, dz = s.z2-s.z1, l2 = dx*dx+dz*dz;
  let t = l2 > 0 ? ((px-s.x1)*dx + (pz-s.z1)*dz)/l2 : 0;
  t = t < 0 ? 0 : t > 1 ? 1 : t;
  const cx = s.x1+t*dx, cz = s.z1+t*dz, ax = px-cx, az = pz-cz;
  return ax*ax + az*az;
}
function build(items, depth) {
  if (!items.length) return null;
  const ax = depth % 2, key = ax === 0 ? 'mx' : 'mz';
  items.sort((a,b) => a[key] - b[key]);
  const m = items.length >> 1, it = items[m];
  return { s: it.s, mx: it.mx, mz: it.mz, ax,
    left: build(items.slice(0,m), depth+1), right: build(items.slice(m+1), depth+1) };
}
function makeTree(segs) {
  let mh = 0;
  const items = segs.map(s => {
    const h = Math.hypot(s.x2-s.x1, s.z2-s.z1) * 0.5;
    if (h > mh) mh = h;
    return { s, mx: (s.x1+s.x2)*0.5, mz: (s.z1+s.z2)*0.5 };
  });
  return { root: build(items, 0), margin: mh };
}
function nearest(tree, qx, qz, maxD) {
  let best = maxD*maxD, col = 0, found = false;
  const mg = tree.margin;
  function search(n) {
    if (!n) return;
    const d2 = pointSegDist2(qx, qz, n.s);
    if (d2 < best) { best = d2; col = n.s.color; found = true; }
    const sv = n.ax === 0 ? n.mx : n.mz, qv = n.ax === 0 ? qx : qz, df = qv - sv;
    const first = df < 0 ? n.left : n.right, second = df < 0 ? n.right : n.left;
    search(first);
    const m = Math.abs(df) - mg;
    if (m < 0 || m*m < best) search(second);
  }
  search(tree.root);
  return found ? { color: col, dist: Math.sqrt(best) } : null;
}
self.onmessage = function (ev) {
  const d = ev.data;
  const tree = makeTree(tessellate(d.ex, d.ez, d.color, d.maxSegLen));
  const W = d.width, j0 = d.j0, j1 = d.j1;
  const band = new Uint8Array((j1 - j0) * W * 4);
  for (let j = j0; j < j1; j++) {
    const mN = d.minZ + ((j + 0.5) / d.height) * d.spanZ;
    const rowBase = (j - j0) * W * 4;
    for (let i = 0; i < W; i++) {
      const east = d.minX + ((i + 0.5) / W) * d.spanX;
      const r = nearest(tree, east, mN, d.maxDist);
      const o = rowBase + i * 4;
      if (r) {
        band[o] = (r.color >> 16) & 0xff;
        band[o+1] = (r.color >> 8) & 0xff;
        band[o+2] = r.color & 0xff;
        band[o+3] = Math.min(254, Math.round((r.dist / d.maxDist) * 254));
      } else {
        band[o+3] = 255;
      }
    }
  }
  self.postMessage({ band: band, j0: j0, j1: j1 }, [band.buffer]);
};
`;

let _workerUrl: string | null = null;
function workerUrl(): string {
  if (!_workerUrl) {
    _workerUrl = URL.createObjectURL(new Blob([WORKER_SRC], { type: "application/javascript" }));
  }
  return _workerUrl;
}

/**
 * Build a SPATIAL distance-field + color texture from the DXF edges — rows are
 * split across several parallel Web Workers, so the main thread stays free and
 * the build is fast.
 */
export async function buildDxfTexture(
  edges: EdgeSegment[],
  onProgress?: (msg: string) => void
): Promise<DxfTexture | null> {
  if (edges.length === 0) return null;

  let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;
  for (const e of edges) {
    if (e.x1 < minX) minX = e.x1; if (e.x2 < minX) minX = e.x2;
    if (e.x1 > maxX) maxX = e.x1; if (e.x2 > maxX) maxX = e.x2;
    if (e.z1 < minZ) minZ = e.z1; if (e.z2 < minZ) minZ = e.z2;
    if (e.z1 > maxZ) maxZ = e.z1; if (e.z2 > maxZ) maxZ = e.z2;
  }
  const pad = DXF_TEX_MAX_DIST;
  minX -= pad; maxX += pad; minZ -= pad; maxZ += pad;
  const spanX = maxX - minX, spanZ = maxZ - minZ;
  const texel = Math.max(spanX, spanZ) / MAX_TEX_DIM;
  const width = Math.max(1, Math.ceil(spanX / texel));
  const height = Math.max(1, Math.ceil(spanZ / texel));

  // serialize edges into typed arrays (cheap structured-clone copy per worker).
  const n = edges.length;
  const ex = new Float32Array(n * 2);
  const ez = new Float32Array(n * 2);
  const color = new Uint32Array(n);
  for (let i = 0; i < n; i++) {
    const e = edges[i];
    ex[i * 2] = e.x1; ex[i * 2 + 1] = e.x2;
    ez[i * 2] = e.z1; ez[i * 2 + 1] = e.z2;
    color[i] = e.color >>> 0;
  }

  const hw = (typeof navigator !== "undefined" && (navigator as any).hardwareConcurrency) || 4;
  const nWorkers = Math.max(1, Math.min(8, hw, height));
  onProgress?.(`Building DXF texture ${width}x${height} (texel≈${texel.toFixed(2)}m) with ${nWorkers} workers…`);

  const t0 = (typeof performance !== "undefined" ? performance.now() : 0);
  const data = new Uint8Array(width * height * 4);
  const rowsPer = Math.ceil(height / nWorkers);

  const url = workerUrl();
  const jobs: Promise<void>[] = [];
  for (let w = 0; w < nWorkers; w++) {
    const j0 = w * rowsPer;
    const j1 = Math.min(height, j0 + rowsPer);
    if (j0 >= j1) break;
    jobs.push(
      new Promise<void>((resolve, reject) => {
        const worker = new Worker(url);
        worker.onmessage = (ev: MessageEvent) => {
          const band = ev.data.band as Uint8Array;
          data.set(band, ev.data.j0 * width * 4);
          worker.terminate();
          resolve();
        };
        worker.onerror = (e) => { worker.terminate(); reject(e); };
        worker.postMessage({
          ex, ez, color, width, height, j0, j1,
          minX, minZ, spanX, spanZ, maxDist: DXF_TEX_MAX_DIST, maxSegLen: MAX_SEG_LEN,
        });
      })
    );
  }
  await Promise.all(jobs);

  const ms = (typeof performance !== "undefined" ? performance.now() : 0) - t0;
  onProgress?.(`DXF texture done in ${ms.toFixed(0)}ms (${nWorkers} workers, off main-thread)`);

  const texture = new DataTexture(data, width, height, RGBAFormat, UnsignedByteType);
  texture.magFilter = NearestFilter;
  texture.minFilter = NearestFilter;
  texture.needsUpdate = true;

  return { texture, minX, minZ, spanX, spanZ, width, height };
}
